import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Calendar, MapPin, Clock, Share2, Download, Heart, User, Users as UsersIcon, BookUser } from "lucide-react";
import Countdown from "@/components/Countdown";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const navigate = useNavigate();
  const [evento, setEvento] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEvento();
  }, []);

  const fetchEvento = async () => {
    try {
      const response = await axios.get(`${API}/evento`);
      setEvento(response.data);
    } catch (error) {
      console.error("Error fetching event:", error);
    } finally {
      setLoading(false);
    }
  };

  const compartirWhatsApp = () => {
    // Copiar la URL al clipboard
    navigator.clipboard.writeText("prepazuno93-96.com")
      .then(() => {
        toast.success("¡Enlace copiado! prepazuno93-96.com - Listo para compartir");
      })
      .catch(() => {
        // Fallback para navegadores que no soporten clipboard API
        const textArea = document.createElement("textarea");
        textArea.value = "prepazuno93-96.com";
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        toast.success("¡Enlace copiado! prepazuno93-96.com - Listo para compartir");
      });
  };

  const agregarAlCalendario = () => {
    const evento = {
      title: "XXX Aniversario Generación 93-96",
      description: "Reunión de exalumnos",
      location: "Terraza Frevo, Av. La Tijera 155",
      start: "20260620T200000",
      end: "20260621T020000"
    };

    const googleCalUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(
      evento.title
    )}&dates=${evento.start}/${evento.end}&details=${encodeURIComponent(
      evento.description
    )}&location=${encodeURIComponent(evento.location)}`;

    window.open(googleCalUrl, "_blank");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-zuno-wine"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-warm-bone">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url('https://res.cloudinary.com/dei3bnrcu/image/upload/v1779548842/reunion93-96/home/hero_background.jpg')`,
            backgroundSize: "cover",
            backgroundPosition: "center"
          }}
        >
          <div className="absolute inset-0 hero-gradient"></div>
        </div>

        <div className="relative z-10 container mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="text-center md:text-left">
              <img
                src="https://res.cloudinary.com/dei3bnrcu/image/upload/v1779548843/reunion93-96/home/logo_xxx_aniversario.png"
                alt="Logo Zuno"
                className="w-64 md:w-80 mx-auto md:mx-0 mb-8 drop-shadow-2xl"
                data-testid="event-logo"
              />
              <h1 className="text-5xl md:text-7xl font-bold text-zuno-wine mb-6 tracking-tight">
                {evento?.nombre}
              </h1>
              <p className="text-xl md:text-2xl text-slate-600 mb-4">
                {evento?.escuela}
              </p>
              <div className="flex flex-col gap-3 text-lg text-slate-700 mb-4">
                <div className="flex items-center gap-3 justify-center md:justify-start">
                  <Calendar className="text-zuno-wine" size={24} />
                  <span>{evento?.fecha}</span>
                </div>
                <div className="flex items-center gap-3 justify-center md:justify-start">
                  <Clock className="text-zuno-wine" size={24} />
                  <span>{evento?.hora}</span>
                </div>
                <div className="flex items-center gap-3 justify-center md:justify-start">
                  <MapPin className="text-zuno-wine" size={24} />
                  <span>{evento?.lugar}</span>
                </div>
                <div className="flex items-center gap-3 justify-center md:justify-start">
                  <User className="text-zuno-wine" size={24} />
                  <span><strong>Código de Vestimenta:</strong> Formal</span>
                </div>
                <div className="flex items-center gap-3 justify-center md:justify-start">
                  <UsersIcon className="text-zuno-wine" size={24} />
                  <span><strong>Restricción:</strong> Solo Adultos</span>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start flex-wrap">
                <Button
                  onClick={() => navigate("/comprar")}
                  className="btn-primary"
                  size="lg"
                  data-testid="buy-tickets-btn"
                >
                  Comprar Boletos
                </Button>
                <Button
                  onClick={() => navigate("/eventos")}
                  variant="outline"
                  className="btn-secondary"
                  size="lg"
                  data-testid="eventos-btn"
                >
                  <Calendar className="mr-2" size={20} />
                  Nuestros Eventos
                </Button>
                <Button
                  onClick={() => navigate("/sponsors-publicos")}
                  variant="outline"
                  className="btn-secondary"
                  size="lg"
                  data-testid="view-sponsors-btn"
                >
                  Sponsors
                </Button>
                <Button
                  onClick={compartirWhatsApp}
                  variant="outline"
                  className="btn-secondary"
                  size="lg"
                  data-testid="share-whatsapp-btn"
                >
                  <Share2 className="mr-2" size={20} />
                  Compartir
                </Button>
              </div>
            </div>

            <div className="hidden md:block">
              <img
                src="https://res.cloudinary.com/dei3bnrcu/image/upload/v1779548844/reunion93-96/home/flyer_evento.jpg"
                alt="Flyer del Evento"
                className="rounded-2xl shadow-2xl"
                data-testid="event-flyer"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Countdown Section */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url("/graduation-bg.jpg")',
            filter: 'brightness(0.4)'
          }}
        ></div>
        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-br from-zuno-wine/60 to-slate-900/70"></div>
        
        <div className="container mx-auto px-6 md:px-12 lg:px-24 relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-center text-white mb-12 drop-shadow-lg">
            Cuenta Regresiva
          </h2>
          <Countdown targetDate="2026-06-20T20:00:00" />
        </div>
      </section>

      {/* Event Details */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="glass-card rounded-3xl p-8 md:p-12">
            <h2 className="text-4xl md:text-5xl font-bold text-zuno-wine mb-8">
              Sobre el Evento
            </h2>
            <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-6 mb-8">
              <h3 className="text-2xl font-semibold text-zuno-wine mb-4">
                El Boleto Incluye:
              </h3>
              <p className="text-lg text-slate-700 leading-relaxed">
                {evento?.incluye}
              </p>
            </div>

            <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-8">
              {evento?.descripcion}
            </p>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <h3 className="text-2xl font-semibold text-zuno-wine mb-4">
                  Detalles
                </h3>
                <div className="space-y-3 text-slate-700">
                  <p>
                    <strong>Fecha:</strong> {evento?.fecha}
                  </p>
                  <p>
                    <strong>Hora:</strong> {evento?.hora}
                  </p>
                  <p>
                    <strong>Lugar:</strong>{" "}
                    <a 
                      href="https://www.facebook.com/FrevoEventos" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-zuno-wine hover:underline"
                      onClick={(e) => {
                        e.preventDefault();
                        window.open('https://www.facebook.com/FrevoEventos', '_blank', 'noopener,noreferrer');
                      }}
                    >
                      {evento?.lugar}
                    </a>
                  </p>
                  <p>
                    <strong>Dirección:</strong> {evento?.direccion}
                  </p>
                  <p>
                    <strong>Código de Vestimenta:</strong> Formal
                  </p>
                  <p>
                    <strong>Restricción:</strong> Solo Adultos
                  </p>
                  <p className="text-2xl font-bold text-zuno-wine mt-6">
                    Precio: ${evento?.precio_boleto} MXN
                  </p>
                </div>
                <div className="flex gap-4 mt-6">
                  <Button
                    onClick={agregarAlCalendario}
                    variant="outline"
                    className="flex items-center gap-2"
                    data-testid="add-to-calendar-btn"
                  >
                    <Download size={18} />
                    Agregar al Calendario
                  </Button>
                </div>
              </div>

              <div className="rounded-2xl overflow-hidden shadow-lg h-80">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3733.5!2d-103.4454!3d20.5297!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8428ad4f00000001%3A0x1234567890abcdef!2sAv.%20La%20Tijera%20155%2C%20Los%20Tulipanes%2C%2045647%20La%20Tijera%2C%20Jal.!5e0!3m2!1ses!2smx!4v1712098800000!5m2!1ses!2smx"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Mapa del evento - Av. La Tijera 155"
                  data-testid="event-map"
                ></iframe>
              </div>
              
              {/* Enlace alternativo a Google Maps */}
              <div className="mt-4 text-center">
                <a 
                  href="https://maps.google.com/?q=Frevo+Terraza+Eventos+Av+La+Tijera+155+Tlajomulco+Jalisco"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-zuno-wine hover:text-zuno-wine/80 underline flex items-center justify-center gap-2"
                  data-testid="open-maps-link"
                  onClick={(e) => {
                    e.preventDefault();
                    window.open('https://maps.google.com/?q=Frevo+Terraza+Eventos+Av+La+Tijera+155+Tlajomulco+Jalisco', '_blank', 'noopener,noreferrer');
                  }}
                >
                  <MapPin size={18} />
                  Abrir en Google Maps
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        {/* Background Image - stretched */}
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: 'url("https://res.cloudinary.com/dei3bnrcu/image/upload/v1779548871/reunion93-96/home/prepa_entrada_background.jpg")',
            backgroundSize: '100% 100%',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.3
          }}
        ></div>
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-br from-zuno-wine/85 to-slate-800/85"></div>
        
        <div className="container mx-auto px-6 md:px-12 lg:px-24 relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-12 text-white">
            ¿Qué quieres hacer?
          </h2>
          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-6">
            <Button
              onClick={() => navigate("/comprar")}
              variant="secondary"
              className="h-auto py-8 flex flex-col items-center gap-4 bg-white text-zuno-wine hover:bg-slate-100 rounded-xl shadow-xl"
              data-testid="quick-action-buy"
            >
              <Calendar size={40} />
              <span className="text-lg font-semibold text-center leading-tight">
                Comprar<br />Boletos
              </span>
            </Button>
            <Button
              onClick={() => navigate("/asistentes")}
              variant="secondary"
              className="h-auto py-8 flex flex-col items-center gap-4 bg-white text-zuno-wine hover:bg-slate-100 rounded-xl shadow-xl"
              data-testid="quick-action-attendees"
            >
              <MapPin size={40} />
              <span className="text-lg font-semibold text-center leading-tight">
                Ver<br />Asistentes
              </span>
            </Button>
            <Button
              onClick={() => navigate("/sponsors")}
              variant="secondary"
              className="h-auto py-8 flex flex-col items-center gap-4 bg-white text-zuno-wine hover:bg-slate-100 rounded-xl shadow-xl"
              data-testid="quick-action-sponsor"
            >
              <Share2 size={40} />
              <span className="text-lg font-semibold">Ser Sponsor</span>
            </Button>
            <Button
              onClick={() => navigate("/donaciones")}
              variant="secondary"
              className="h-auto py-8 flex flex-col items-center gap-4 bg-white text-zuno-wine hover:bg-slate-100 rounded-xl shadow-xl"
              data-testid="quick-action-donate"
            >
              <Heart size={40} />
              <span className="text-lg font-semibold text-center leading-tight">
                ¿Cómo Puedo<br />Ayudar?
              </span>
            </Button>
            <Button
              onClick={() => navigate("/galeria")}
              variant="secondary"
              className="h-auto py-8 flex flex-col items-center gap-4 bg-white text-zuno-wine hover:bg-slate-100 rounded-xl shadow-xl"
              data-testid="quick-action-gallery"
            >
              <Download size={40} />
              <span className="text-lg font-semibold">Ver Galería</span>
            </Button>
            <Button
              onClick={() => navigate("/directorio")}
              variant="secondary"
              className="h-auto py-8 flex flex-col items-center gap-4 bg-white text-zuno-wine hover:bg-slate-100 rounded-xl shadow-xl"
              data-testid="quick-action-directorio"
            >
              <BookUser size={40} />
              <span className="text-lg font-semibold">Directorio</span>
            </Button>
            <Button
              onClick={() => navigate("/contacto")}
              variant="secondary"
              className="h-auto py-8 flex flex-col items-center gap-4 bg-white text-zuno-wine hover:bg-slate-100 rounded-xl shadow-xl"
              data-testid="quick-action-contact"
            >
              <Share2 size={40} />
              <span className="text-lg font-semibold">Contáctanos</span>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 text-center">
          <p className="text-lg mb-4">
            XXX Aniversario de Generación 93-96
          </p>
          <p className="text-sm text-slate-400">
            Preparatoria José Gpe. Zuno Hernández A.C.
          </p>
          <p className="text-sm text-slate-500 mt-4">
            © 2026 Todos los derechos reservados
          </p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
